import { isBrowser } from "./types";

/**
 * Estado de conectividad.
 *
 * Advertencia deliberada: `navigator.onLine === true` significa "hay una interfaz de
 * red activa", no "el servidor responde". Un teléfono conectado a un wifi sin salida
 * reporta `true`. Por eso la cola (`outbox.ts`) nunca decide por esta señal: intenta
 * enviar y aprende del resultado. Esto sirve para dos cosas legítimas: mostrar el
 * estado al usuario y disparar un intento cuando la red *vuelve*.
 */
export function isOnline(): boolean {
  if (!isBrowser()) return true;
  return navigator.onLine !== false;
}

/** Devuelve la función para desuscribirse. */
export function subscribeToNetwork(
  listener: (online: boolean) => void,
): () => void {
  if (!isBrowser()) return () => {};

  const handleOnline = () => listener(true);
  const handleOffline = () => listener(false);

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}

export type ConnectionQuality = "unknown" | "slow" | "moderate" | "fast";

type NetworkInformation = {
  effectiveType?: string;
  saveData?: boolean;
};

const readConnection = (): NetworkInformation | undefined => {
  if (!isBrowser()) return undefined;
  return (navigator as Navigator & { connection?: NetworkInformation }).connection;
};

/**
 * Solo Chromium la expone. Se usa para decidir si vale la pena subir una foto ahora
 * o dejarla en cola (§10.3, planes de datos limitados), nunca para bloquear nada.
 */
export function getConnectionQuality(): ConnectionQuality {
  const effectiveType = readConnection()?.effectiveType;
  if (!effectiveType) return "unknown";
  if (effectiveType === "slow-2g" || effectiveType === "2g") return "slow";
  if (effectiveType === "3g") return "moderate";
  return "fast";
}

/** El usuario pidió al sistema operativo ahorrar datos. Se respeta. */
export function prefersReducedData(): boolean {
  return readConnection()?.saveData === true;
}
